import { useEffect, useState } from 'react';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import {
  useBreadcrumbsContext,
  useHeaderContext,
} from '../../../../app/context';
import {
  SimulationMainContainer,
  ProductSelectorContainer,
  StyledCentralized,
} from './styles';
import { SimulationV2Step } from 'modules/products/pages/ApplicationRequest/SimulationV2Step';
import { ApplicationRequestContext } from 'modules/products/pages/ApplicationRequest/ApplicationRequest';
import type { IApplicationRequestContext } from 'modules/products/pages/ApplicationRequest/ApplicationRequest';
import { defaultLoanGuaranteeFormValues } from 'modules/products/pages/ApplicationRequest/ConsultaFgts/interfaces';
import { useProductsService } from 'modules/products/services/hooks';
import { TProducts } from 'modules/products/context';
import { filterVisibleProducts } from 'modules/products/helpers/productVisibility';
import {
  TSimulatorV2Request,
  TSimulatorV2Response,
} from 'app/services/simulatorHttpServiceV2';
import { routingPath } from 'app/routes';
import { BoxSection, CustomSelect, Spinner } from '../../../../app/components';
import PanelTitle from 'app/components/typography/heading/PanelTitle/PanelTitle';
import FgtsStep from 'modules/products/pages/ApplicationRequest/FgtsStep/FgtsStep';
import ConsignedStep from 'modules/products/pages/ApplicationRequest/ConsignedStep/ConsignedStep';
import {
  ConsigneeOrganType,
  IConsignedList,
  IConsignedProduct,
  IConsigned,
  TypeProduct,
} from 'modules/products/pages/ApplicationRequest/ConsignedStep/interfaces';
import { IFgtsPeriodsReview } from 'modules/products/pages/ApplicationRequest/FgtsStep/interfaces';
import EmptyState from 'app/components/EmptyState/EmptyState';
import EmptyFile from 'app/components/Icons/svgs/EmptyFile.svg';

const SimulationMain = () => {
  const { setItems, resetBreadcrumbs } = useBreadcrumbsContext();
  const { setTitle, resetTitle } = useHeaderContext();
  const { getProductsList, getProductsListLoading, getProduct } =
    useProductsService();

  const [products, setProducts] = useState<TProducts[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<TProducts | null>(
    null,
  );
  const [simulationV2, setSimulationV2] = useState<TSimulatorV2Response | null>(
    null,
  );
  const [simulationV2Request, setSimulationV2Request] =
    useState<TSimulatorV2Request | null>(null);
  const [tacValidationError, setTacValidationError] = useState<string>('');
  const [consignedList, setConsignedList] = useState<IConsignedList>();
  const [fgtsPeriods, setFgtsPeriods] = useState<IFgtsPeriodsReview[]>();
  const [fgtsValidationErrors, setFgtsValidationErrors] =
    useState<boolean>(false);
  const [consignedError, setConsignedError] = useState<string>('');
  const [consignedProduct, setConsignedProduct] = useState<IConsignedProduct>();
  const [consignedConfig, setConsignedConfig] = useState<IConsigned>();
  const [showConsignedSimulator, setShowConsignedSimulator] =
    useState<boolean>(false);

  useEffect(() => {
    setItems(
      [{ to: routingPath.simulation.main, label: 'Simulador' }],
      <CalculateOutlinedIcon />,
    );
    return resetBreadcrumbs;
  }, []);

  useEffect(() => {
    setTitle('Simulador');
    return resetTitle;
  }, []);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    const result = await getProductsList();

    if (!result?.content) {
      return;
    }

    const productsWithDetails = await Promise.all(
      result.content.map(async (product) => {
        if (product.organ_type !== ConsigneeOrganType.CT || product.sub_type) {
          return product;
        }

        const fullProduct = await getProduct(product.id, true);
        return fullProduct?.data ?? product;
      }),
    );

    setProducts(filterVisibleProducts(productsWithDetails));
  };

  const clearStates = () => {
    setSimulationV2(null);
    setSimulationV2Request(null);
    setTacValidationError('');

    // Limpar estados do FGTS
    setConsignedList(undefined);
    setFgtsPeriods(undefined);
    setFgtsValidationErrors(false);
    setConsignedError('');
    setConsignedProduct(undefined);

    // Limpar estados do consignado
    setConsignedConfig(undefined);
    setShowConsignedSimulator(false);
  };

  const handleProductChange = (event: {
    currentTarget: { name: string; value: { value: string; label: string } };
  }) => {
    const selectedValue = event.currentTarget.value;
    if (selectedValue && selectedValue.value) {
      const product = products.find((p) => p.id === selectedValue.value);
      if (product) {
        setSelectedProduct(product);
        clearStates();
      }
    } else {
      setSelectedProduct(null);
      clearStates();
    }
  };

  const isFgts = selectedProduct?.organ_type === ConsigneeOrganType.FGTS;
  const isConsigned =
    !!selectedProduct?.organ_type &&
    [ConsigneeOrganType.CT, ConsigneeOrganType.SIAPE].includes(
      selectedProduct.organ_type,
    );

  // Verifica se o consignado está autorizado para empréstimo
  const isLoanAuthorized: boolean =
    (consignedProduct?.authorization?.details?.filter(
      (item) => item.type_product === TypeProduct.LOAN,
    )[0]?.authorized &&
      !!consignedProduct?.available_balance) ||
    false;

  const shouldShowFgtsStep = isFgts && !fgtsPeriods?.length;
  const shouldShowConsignedStep =
    isConsigned && (!isLoanAuthorized || !showConsignedSimulator);

  const productOptions = products.map((product) => ({
    value: product.id,
    label: product.name,
  }));

  const selectedProductValue = selectedProduct
    ? {
        value: selectedProduct.id,
        label: selectedProduct.name,
      }
    : undefined;

  const handleForward = () => {
    // Se for consignado e estiver autorizado, mostrar o simulador
    if (isConsigned && isLoanAuthorized && !showConsignedSimulator) {
      setShowConsignedSimulator(true);
    }
    // O FgtsStep já atualiza fgtsPeriods através do contexto
    // Quando fgtsPeriods tiver períodos, o simulador será mostrado automaticamente
  };

  const contextValue: IApplicationRequestContext = {
    activeStep: 0,
    onForward: handleForward,
    fgtsQueryBalanceCheckId: undefined,
    setFgtsQueryBalanceCheckId: () => {},
    onBack: () => {
      // Voltar para o FgtsStep se estiver no simulador e for FGTS
      if (isFgts && fgtsPeriods?.length) {
        setFgtsPeriods(undefined);
      }
      // Voltar para o ConsignedStep se estiver no simulador e for consignado
      if (isConsigned && showConsignedSimulator) {
        setShowConsignedSimulator(false);
      }
    },
    onBackTwoSteps: () => {},
    hasVariablesStep: false,
    hasCustomVariables: false,
    setHasCustomVariables: () => {},
    borrowerData: null,
    setBorrowerData: () => {},
    borrowerQualification: null,
    setBorrowerQualification: () => {},
    subscribers: null,
    setSubscribers: () => {},
    product: selectedProduct,
    setProduct: () => {},
    funding: null,
    hasMultiplesFundings: false,
    setFunding: () => {},
    beneficiaryAccount: null,
    setBeneficiaryAccount: () => {},
    customVariables: null,
    setCustomVariables: () => {},
    custodian: null,
    setCustodian: () => {},
    customCcbTemplateId: null,
    setCustomCcbTemplateId: () => {},
    basePremiumAmount: null,
    simulationV2,
    simulationV2Request,
    setSimulationV2: (
      simulation: TSimulatorV2Response | null,
      simulationRequest: TSimulatorV2Request | null,
    ) => {
      setSimulationV2(simulation);
      setSimulationV2Request(simulationRequest);
    },
    simulateWithDifferentDisbursementDate: () => {},
    billetValidationResponse: null,
    setBilletValidationResponse: () => {},
    qrcodeEmvValidationResponse: null,
    setQrcodeEmvValidationResponse: () => {},
    originator: null,
    updateOriginator: () => {},
    splitPayments: [],
    setSplitPayments: () => {},
    isClonning: null,
    consignedError,
    consignedProduct,
    setConsignedProduct,
    consignedConfig,
    setConsignedConfig,
    consignedList,
    setConsignedList,
    fgtsPeriods,
    setFgtsPeriods,
    fgtsValidationErrors,
    setFgtsValidationErrors,
    loanGuaranteeInfo: undefined,
    setLoanGuaranteeInfo: () => {},
    loanGuaranteeFormValues: defaultLoanGuaranteeFormValues,
    setLoanGuaranteeFormValues: () => {},
    simulationPersistedNumPayments: null,
    setSimulationPersistedNumPayments: () => {},
    setConsignedError,
    clearProductSelection: () => {},
    cobransaasResponse: undefined,
    setCobransaasResponse: () => {},
    tacValidationError,
    setTacValidationError,
    getElegibilityRules: async () => null,
    setElegibilityError: () => {},
    elegibilityError: { message: '', description: '' },
    elegibilities: undefined,
    setElegibilities: () => {},
  };

  return (
    <ApplicationRequestContext.Provider value={contextValue}>
      <PanelTitle>Simulador</PanelTitle>
      <BoxSection>
        <SimulationMainContainer>
          {!getProductsListLoading && productOptions.length === 0 ? (
            <EmptyState title="Não há produtos cadastrados para simulação." />
          ) : (
            <>
              <ProductSelectorContainer>
                <CustomSelect
                  labelValue="Selecione um produto"
                  name="product"
                  placeholder="Selecione um produto"
                  value={selectedProductValue}
                  handleInputChange={handleProductChange}
                  options={productOptions}
                  disabled={getProductsListLoading}
                />
              </ProductSelectorContainer>
              {getProductsListLoading ? (
                <StyledCentralized>
                  <Spinner size={48} />
                </StyledCentralized>
              ) : selectedProduct ? (
                <>
                  {shouldShowFgtsStep ? (
                    <FgtsStep isCalcSimulation={true} />
                  ) : shouldShowConsignedStep ? (
                    <ConsignedStep isCalcSimulation={true} />
                  ) : (
                    <SimulationV2Step
                      key={selectedProduct.id}
                      isCalcSimulation={true}
                    />
                  )}
                </>
              ) : (
                <div
                  style={{
                    padding: '24px',
                    textAlign: 'center',
                    marginBottom: '3rem',
                  }}
                >
                  <img src={EmptyFile} width={250} />
                  <p>Selecione um produto para simular.</p>
                </div>
              )}
            </>
          )}
        </SimulationMainContainer>
      </BoxSection>
    </ApplicationRequestContext.Provider>
  );
};

export default SimulationMain;
